import React, { useEffect, useRef, useState } from "react";
import { Prompt, useHistory, useParams } from "react-router-dom";
import { Alert, Button, Card, Input, Modal, Segmented, Skeleton, Space, Typography } from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  LeftOutlined,
  LockOutlined,
  RightOutlined,
  SaveOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import {
  correct,
  deleteEntry,
  getDay,
  getMeta,
  openDay,
  proposeStaff,
  refreshWeather,
  sign,
  updateEntry
} from "./api";
import { normalizeContribution } from "./calc";
import { ContributionCard, DeleteButton, OthersCard, StaffCard, WeatherCard } from "./DaySections";
import { HistoryCard, ObstructionsCard, PhotosCard, SignaturesCard } from "./DayExtras";
import { LIST, dayUrl, shiftDay, todayBerlin } from "./format";
import "./siteDiary.css";

const WEATHER_FIELDS = [
  "temperatureMorning",
  "temperatureNoon",
  "code",
  "conditions",
  "windKmh",
  "wind",
  "precipitationMm",
  "note"
];

const contributionOf = (entry, author) => {
  const found =
    entry.contributions.find(c => String(c.author) === String(author)) ||
    (String(author) === String(entry.mine.author) ? entry.mine : null);
  return { ...normalizeContribution(found || {}), author: String(author) };
};

const formFromEntry = (entry, author) => ({
  weather: Object.fromEntries(WEATHER_FIELDS.map(key => [key, entry.weather?.[key] ?? null])),
  staff: entry.staff.map(row => ({ ...row })),
  contribution: contributionOf(entry, author),
  obstructions: entry.obstructions.map(row => ({ ...row })),
  photoCaptions: {}
});

/** The request body: only the parts that were changed. */
const bodyFrom = (form, dirty, entry) => {
  const body = {};
  if (dirty.has("weather")) body.weather = form.weather;
  if (dirty.has("staff")) body.staff = form.staff;
  if (dirty.has("contribution")) {
    const { author, ...values } = form.contribution;
    body.contribution =
      String(author) === String(entry.mine.author) ? values : { ...values, author };
  }
  if (dirty.has("obstructions")) body.obstructions = form.obstructions;
  if (dirty.has("photoCaptions")) body.photoCaptions = form.photoCaptions;
  return body;
};

// Unsaved edits stay on this device until saved: there is often no signal on site.
const draftKey = id => `siteDiary:draft:${id}`;
const readDraft = id => {
  try {
    return JSON.parse(localStorage.getItem(draftKey(id)) || "null");
  } catch {
    return null;
  }
};
const writeDraft = (id, value) => {
  try {
    if (value) localStorage.setItem(draftKey(id), JSON.stringify(value));
    else localStorage.removeItem(draftKey(id));
  } catch {
    // storage full or blocked: the edit is still on screen
  }
};

const SiteDiaryDay = () => {
  const { t } = useTranslation("dashboard");
  const { projectId, date } = useParams();
  const history = useHistory();
  const [meta, setMeta] = useState(null);
  const [project, setProject] = useState(null);
  const [entry, setEntry] = useState(undefined); // undefined = loading, null = no entry yet
  const [author, setAuthor] = useState(null);
  const [form, setForm] = useState(null);
  const [dirty, setDirty] = useState(new Set());
  const [correcting, setCorrecting] = useState(false);
  const [reasonOpen, setReasonOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(null);
  const leaving = useRef(false);
  const today = todayBerlin();

  const applyEntry = (data, keepAuthor = author) => {
    const who = keepAuthor || data.mine.author;
    setEntry(data);
    setProject(data.projectInfo);
    setAuthor(who);
    setForm(formFromEntry(data, who));
    setDirty(new Set());
    setCorrecting(false);
    writeDraft(data._id, null);
  };

  const start = async () => {
    setBusy("open");
    try {
      const res = await openDay(projectId, date);
      applyEntry(res.data.data, null);
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setEntry(undefined);
    setForm(null);
    (async () => {
      try {
        const [metaRes, dayRes] = await Promise.all([
          meta ? { data: meta } : getMeta(),
          getDay(projectId, date)
        ]);
        if (cancelled) return;
        setMeta(metaRes.data);
        const data = dayRes.data.data;
        if (!data) {
          setProject(dayRes.data.project);
          setEntry(null);
          // Today's page on the phone: start at once
          if (date === today && metaRes.data.can.create) start();
          return;
        }
        applyEntry(data, null);
        const local = readDraft(data._id);
        if (local?.form && local.savedAt > Date.parse(data.updatedAt) && data.can.edit) {
          setForm(local.form);
          setAuthor(local.form.contribution.author);
          setDirty(new Set(local.dirty));
        } else if (local) {
          writeDraft(data._id, null);
        }
      } catch (error) {
        generalErrorAlert(error);
        history.push(`${LIST}?project=${projectId}`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, date]);

  useEffect(() => {
    if (entry && dirty.size && !correcting) {
      writeDraft(entry._id, { form, dirty: [...dirty], savedAt: Date.now() });
    }
  }, [form, dirty]);

  const change = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setDirty(prev => new Set(prev).add(key));
  };

  const save = async () => {
    if (correcting) {
      setReasonOpen(true);
      return;
    }
    setBusy("save");
    try {
      const res = await updateEntry(entry._id, bodyFrom(form, dirty, entry));
      applyEntry(res.data.data);
      Success(t("Saved"));
    } catch (error) {
      if (error?.response?.data?.code === "LOCKED") writeDraft(entry._id, null);
      generalErrorAlert(error);
    } finally {
      setBusy(null);
    }
  };

  const saveCorrection = async () => {
    setBusy("save");
    try {
      const res = await correct(entry._id, { ...bodyFrom(form, dirty, entry), reason });
      setReasonOpen(false);
      setReason("");
      applyEntry(res.data.data);
      Success(t("The correction was saved."));
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(null);
    }
  };

  const switchAuthor = who => {
    if (dirty.has("contribution")) {
      return Modal.confirm({
        title: t("Discard the changes to this part?"),
        onOk: () => {
          setDirty(prev => {
            const next = new Set(prev);
            next.delete("contribution");
            return next;
          });
          setAuthor(who);
          setForm(prev => ({ ...prev, contribution: contributionOf(entry, who) }));
        }
      });
    }
    setAuthor(who);
    setForm(prev => ({ ...prev, contribution: contributionOf(entry, who) }));
  };

  const loadAttendance = async () => {
    setBusy("attendance");
    try {
      const res = await proposeStaff(entry._id);
      change("staff", res.data.staff);
      if (res.data.running?.length) {
        Modal.info({
          title: t("Still clocked in"),
          content: t("Not included yet: {{names}}", { names: res.data.running.join(", ") })
        });
      }
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(null);
    }
  };

  const reloadWeather = async () => {
    const run = async force => {
      setBusy("weather");
      try {
        const res = await refreshWeather(entry._id, force);
        applyEntry(res.data.data);
        if (!res.data.updated && res.data.reason !== "MANUAL") {
          generalErrorAlert({ message: t("The weather service could not be reached.") });
        }
      } catch (error) {
        generalErrorAlert(error);
      } finally {
        setBusy(null);
      }
    };
    if (entry.weather?.source === "MANUAL") {
      Modal.confirm({
        title: t("Replace the weather entered by hand?"),
        onOk: () => run(true)
      });
    } else run(false);
  };

  const signDay = async values => {
    try {
      applyEntry((await sign(entry._id, values)).data.data);
      Success(t("Signed"));
    } catch (error) {
      generalErrorAlert(error);
      throw error;
    }
  };

  const remove = async () => {
    try {
      await deleteEntry(entry._id);
      writeDraft(entry._id, null);
      leaving.current = true;
      history.push(`${LIST}?project=${projectId}`);
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  const go = day => history.push(dayUrl(projectId, day));

  const header = (
    <div className="sdi-header">
      <Button
        icon={<ArrowLeftOutlined />}
        aria-label={t("Back")}
        onClick={() => history.push(`${LIST}?project=${projectId}`)}
      />
      <div className="sdi-header-title">
        <Typography.Title level={4}>{project?.name || t("Site diary")}</Typography.Title>
        <Typography.Text type="secondary">{project?.site}</Typography.Text>
      </div>
      {entry?.can?.delete && <DeleteButton onConfirm={remove} />}
    </div>
  );

  const dayNav = (
    <div className="sdi-daynav">
      <Button
        icon={<LeftOutlined />}
        aria-label={t("Previous day")}
        onClick={() => go(shiftDay(date, -1))}
      />
      <Typography.Text strong className="sdi-daynav-date">
        {dayjs(date).format("dddd, DD.MM.YYYY")}
      </Typography.Text>
      <Button
        icon={<RightOutlined />}
        aria-label={t("Next day")}
        disabled={date >= today}
        onClick={() => go(shiftDay(date, 1))}
      />
    </div>
  );

  if (entry === undefined || (entry && !form)) {
    return (
      <div className="site-diary-page sdi-editor">
        {header}
        {dayNav}
        <Skeleton active />
      </div>
    );
  }

  if (entry === null) {
    return (
      <div className="site-diary-page sdi-editor">
        {header}
        {dayNav}
        <Card className="sdi-card">
          {date > today ? (
            <Typography.Paragraph>
              {t("A diary entry cannot be written in advance.")}
            </Typography.Paragraph>
          ) : meta?.can?.create ? (
            <Space direction="vertical">
              <Typography.Paragraph>
                {t(
                  "There is no entry for this day yet. Weather and staff from the attendance are filled in automatically."
                )}
              </Typography.Paragraph>
              <Button type="primary" size="large" loading={busy === "open"} onClick={start}>
                {t("Start entry for this day")}
              </Button>
            </Space>
          ) : (
            <Typography.Paragraph>{t("There is no entry for this day.")}</Typography.Paragraph>
          )}
        </Card>
      </div>
    );
  }

  const lock = entry.lockInfo;
  const editable = entry.can.edit || correcting;
  const editAll = entry.can.editAll || correcting;
  const authors = [
    { value: String(entry.mine.author), label: t("My part") },
    ...entry.contributions
      .filter(c => String(c.author) !== String(entry.mine.author))
      .map(c => ({ value: String(c.author), label: c.authorName || t("Unknown") }))
  ];

  return (
    <div className="site-diary-page sdi-editor">
      <Prompt
        when={dirty.size > 0 && !leaving.current}
        message={t("You have unsaved changes. Leave anyway?")}
      />
      {header}
      {dayNav}

      {lock.locked ? (
        <Alert
          type={lock.corrected ? "warning" : "info"}
          showIcon
          icon={<LockOutlined />}
          className="sdi-lock"
          message={
            lock.reason === "SIGNED"
              ? t("Locked by signature on {{date}}", {
                  date: dayjs(lock.at || lock.lockAt).format("DD.MM.YYYY HH:mm")
                })
              : t("Locked since {{date}} (edit window over)", {
                  date: dayjs(lock.at || lock.lockAt).format("DD.MM.YYYY HH:mm")
                })
          }
          description={
            <>
              {lock.corrected && <div>{t("Corrected after locking – see the history.")}</div>}
              <div className="sdi-hash">SHA-256 {entry.contentHash}</div>
            </>
          }
          action={
            entry.can.correct && !correcting ? (
              <Button size="small" icon={<EditOutlined />} onClick={() => setCorrecting(true)}>
                {t("Correct")}
              </Button>
            ) : null
          }
        />
      ) : (
        lock.lockAt && (
          <Typography.Paragraph type="secondary" className="sdi-small">
            {t("Can be changed until {{date}}.", {
              date: dayjs(lock.lockAt).format("DD.MM.YYYY HH:mm")
            })}
          </Typography.Paragraph>
        )
      )}
      {correcting && (
        <Alert
          type="warning"
          showIcon
          className="sdi-lock"
          message={t(
            "Correction mode: your changes are saved with a reason and the previous values."
          )}
        />
      )}

      <WeatherCard
        weather={form.weather}
        codes={meta.weatherCodes}
        editable={editable}
        refreshing={busy === "weather"}
        onRefresh={reloadWeather}
        onChange={value => change("weather", value)}
      />

      <StaffCard
        staff={form.staff}
        employees={meta.employees}
        editable={editable}
        loading={busy === "attendance"}
        onAttendance={loadAttendance}
        onChange={value => change("staff", value)}
      />

      <ContributionCard
        title={
          String(author) === String(entry.mine.author)
            ? t("My entry")
            : t("Entry of {{name}}", {
                name: authors.find(a => a.value === String(author))?.label || ""
              })
        }
        extra={
          editAll && authors.length > 1 ? (
            <Segmented
              size="small"
              value={String(author)}
              options={authors}
              onChange={switchAuthor}
            />
          ) : null
        }
        contribution={form.contribution}
        editable={editable && (String(author) === String(entry.mine.author) || editAll)}
        voiceConfigured={meta.voiceConfigured}
        voiceTranslateTo={meta.voiceTranslateTo}
        onChange={value => change("contribution", value)}
      />

      <OthersCard merged={entry.merged} exclude={author} />

      <ObstructionsCard
        entryId={entry._id}
        obstructions={form.obstructions}
        editable={editable}
        canNotice={entry.can.notice}
        dirty={dirty.size > 0}
        clientEmail={project?.clientEmail || ""}
        voiceConfigured={meta.voiceConfigured}
        voiceTranslateTo={meta.voiceTranslateTo}
        onChange={value => change("obstructions", value)}
        onEntry={data => applyEntry(data)}
      />

      <PhotosCard
        entry={entry}
        editable={entry.can.edit}
        captions={form.photoCaptions}
        onCaption={(id, caption) =>
          change("photoCaptions", { ...form.photoCaptions, [id]: caption })
        }
        onEntry={data => applyEntry(data)}
      />

      <SignaturesCard entry={entry} dirty={dirty.size > 0} onSign={signDay} />

      <HistoryCard entry={entry} />

      {editable && (
        <div className="sdi-savebar">
          {correcting && (
            <Button
              onClick={() => {
                setCorrecting(false);
                setForm(formFromEntry(entry, author));
                setDirty(new Set());
              }}
            >
              {t("Cancel")}
            </Button>
          )}
          <Button
            type="primary"
            size="large"
            icon={<SaveOutlined />}
            disabled={!dirty.size}
            loading={busy === "save"}
            onClick={save}
          >
            {correcting ? t("Save correction") : t("Save")}
          </Button>
        </div>
      )}

      <Modal
        rootClassName="site-diary-page"
        open={reasonOpen}
        onCancel={() => setReasonOpen(false)}
        title={t("Reason for the correction")}
        footer={[
          <Button key="cancel" onClick={() => setReasonOpen(false)}>
            {t("Cancel")}
          </Button>,
          <Button
            key="ok"
            type="primary"
            disabled={reason.trim().length < 5}
            loading={busy === "save"}
            onClick={saveCorrection}
          >
            {t("Save correction")}
          </Button>
        ]}
      >
        <Typography.Paragraph type="secondary">
          {t(
            "The day is evidence. The correction is kept with your name, the reason and the previous values."
          )}
        </Typography.Paragraph>
        <Input.TextArea
          autoSize={{ minRows: 3, maxRows: 8 }}
          maxLength={1000}
          value={reason}
          onChange={event => setReason(event.target.value)}
          placeholder={t("e.g. hours of Mr. Kaya corrected after checking the time sheet")}
        />
      </Modal>
    </div>
  );
};

export default SiteDiaryDay;
